import { dragOverHighlight } from "@/common/style/commonStyle";
import { makeStyles, tokens } from "@fluentui/react-components";

// The ACS SendBox's bordered element, isolated from its borderless wrapper ancestors (which also
// contain the field) — only it has a plain, non-Stack text container two levels down. Hoisted so the
// DOM assumption lives in one place: it is composed into the resting, hover/focus and ::after rules
// below, and a change to ACS's markup only has to be made here.
const SENDBOX_BOX =
	"& div:has(> .ms-Stack > div:not(.ms-Stack) [data-ui-id='sendbox-textfield'])";

export const useChatStyle = makeStyles({
	conversation: {
		display: "flex",
		flexDirection: "column",
		marginTop: "10px",
		// Fill the remaining space in the workspace's flex column so the send box sits at the bottom.
		// This replaces an earlier `height: calc(100% - <bar> - var(--variable-container-height) -
		// var(--crm-picker-height))` scheme: those CSS vars were republished (via ResizeObserver) one
		// frame AFTER the CRM picker collapsed, so the conversation height — and the send box with it —
		// snapped a frame late (the collapse "flicker": send box jumped up, then back to the bottom).
		// Flexbox reflows synchronously in the same layout pass as the picker collapse, so the send box
		// no longer moves. (The transcription/hideSendBox path already sized this element with flex:1;
		// this brings the send-box path in line.)
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: 0,
		minHeight: 0,

		"& .fui-ChatMessage": {
			"& .fui-ChatMessage": {
				"& > div": {
					width: "auto !important",
				},
			},
		},
	},
	textEditor: {
		// Pin the composer to the bottom of the conversation column. The thread area above it is sized
		// by a measured height that updates one frame late on resize; an auto top margin keeps the send
		// box glued to the bottom during that frame instead of riding the thread's transient height.
		marginTop: "auto",
		paddingLeft: "calc((50% - 130px) / 2)",
		paddingRight: "calc((50% - 132px) / 2)",
		[`@container workspacePanel (width <= 480px)`]: {
			paddingLeft: "calc((50% - 160px) / 2)",
		},
		// Align the ACS SendBox's border with the "Attach file" (Fluent v9) button below it — see
		// SENDBOX_BOX above for how the bordered element is targeted.
		//
		// Resting: ACS colours the border from the v8 palette's `neutralSecondary` (#605e5c), darker than
		// the button's `colorNeutralStroke1` — match the button.
		// (Longhand border/margin/radius props are used throughout rather than the `shorthands` helper:
		// this app resolves Griffel at runtime, where the deprecated helper is the only working shorthand
		// path — so we spell the longhands out instead to keep static analysis clean.)
		[`${SENDBOX_BOX}:not(:hover):not(:focus-within)`]: {
			borderTopColor: tokens.colorNeutralStroke1,
			borderRightColor: tokens.colorNeutralStroke1,
			borderBottomColor: tokens.colorNeutralStroke1,
			borderLeftColor: tokens.colorNeutralStroke1,
		},
		// Hover/focus: ACS thickens the border to a full 2px brand-blue box, which reflows the composer and
		// makes it flicker. Instead — the way the search input does it — keep the neutral frame at its
		// resting 1px width (margins unchanged too, so the frame is identical to the resting box) and draw
		// the brand (purple) bottom accent with an absolutely-positioned ::after. Being out of flow, the
		// accent adds no height and moves nothing. `position: relative` anchors that ::after.
		[`${SENDBOX_BOX}:hover, ${SENDBOX_BOX}:focus-within`]: {
			position: "relative",
			borderTopWidth: "1px",
			borderTopStyle: "solid",
			borderTopColor: tokens.colorNeutralStroke1,
			borderRightWidth: "1px",
			borderRightStyle: "solid",
			borderRightColor: tokens.colorNeutralStroke1,
			borderBottomWidth: "1px",
			borderBottomStyle: "solid",
			borderBottomColor: tokens.colorNeutralStroke1,
			borderLeftWidth: "1px",
			borderLeftStyle: "solid",
			borderLeftColor: tokens.colorNeutralStroke1,
			marginTop: "1px",
			marginRight: "1px",
			marginBottom: "1px",
			marginLeft: "1px",
		},
		// The brand bottom accent, drawn as an overlay aligned to the box's border-box edges. `bottom:-1px`
		// sits it on the frame's bottom edge; the bottom radii match the container so the corners curve.
		[`${SENDBOX_BOX}:hover::after, ${SENDBOX_BOX}:focus-within::after`]: {
			content: '""',
			position: "absolute",
			left: "-1px",
			right: "-1px",
			bottom: "-1px",
			// Deliberately tied to the corner radius: the overlay only needs to be tall enough to cover the
			// curved part of the bottom corners, so it tracks whatever the container's radius is.
			height: tokens.borderRadiusMedium,
			borderBottomWidth: "2px",
			borderBottomStyle: "solid",
			borderBottomColor: tokens.colorCompoundBrandStroke,
			borderBottomLeftRadius: tokens.borderRadiusMedium,
			borderBottomRightRadius: tokens.borderRadiusMedium,
			pointerEvents: "none",
		},
		// The text field's fieldGroup ships with an opaque background but square corners (no radius).
		// Inside the rounded border its square corners overlap the arc and eat into the border there,
		// making the corners look thinner than the straight edges. Rounding it to the container's radius
		// keeps the border an even weight all the way around. Applies to both composers.
		"& .ms-TextField-fieldGroup": {
			borderTopRightRadius: tokens.borderRadiusMedium,
			borderTopLeftRadius: tokens.borderRadiusMedium,
			borderBottomRightRadius: tokens.borderRadiusMedium,
			borderBottomLeftRadius: tokens.borderRadiusMedium,
		},
	},
	spinner: {
		margin: tokens.spacingHorizontalM,
	},
	sentMessage: {
		justifyContent: "flex-end",
		display: "flex",
		width: "100%",
		alignItems: "flex-end",
	},
	statusIcon: {
		flexShrink: 1,
	},
	avatar: {
		fontSize: "14px",
		fontWeight: 600,
		color: tokens.colorBrandBackgroundInverted,
		right: "auto",
		left: 0,
	},
	webchatAttachmentsContainer: {
		display: "flex",
		flexDirection: "column",
		gap: "8px",
		paddingTop: "6px",
	},
	webchatAttachmentSubContainer: {
		display: "flex",
		gap: "8px",
		flexDirection: "column",
		alignItems: "flex-start",
	},
	announcementMessage: {
		padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalL}`,
		fontSize: tokens.fontSizeBase200,
		wordBreak: "break-word",
		textAlign: "center",
	},
	sendboxRelative: {
		position: "relative",
	},
	dropOverlay: {
		...dragOverHighlight(),
		left: "4px",
		right: "4px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
});
